import type { RootState } from "store";
import type { DrawingToolName, DrawingCoordinates, Point } from "./types";

export function computeInitialCoordinates(
  name: DrawingToolName,
  state: RootState,
): DrawingCoordinates | null {
  const { verticalLineInitialTimestamp, chartPriceRange, visibleCandleCount, timeframeMs } = state.drawing;
  const centerPrice = chartPriceRange ? (chartPriceRange.min + chartPriceRange.max) / 2 : null;

  if (name === "horizontalLine") {
    return centerPrice !== null ? { kind: "single-price", price: centerPrice } : null;
  }

  if (name === "verticalLine") {
    return verticalLineInitialTimestamp !== null
      ? { kind: "single-timestamp", timestamp: verticalLineInitialTimestamp }
      : null;
  }

  if (centerPrice === null || verticalLineInitialTimestamp === null || !timeframeMs) return null;

  const candleOffset = visibleCandleCount ? Math.max(2, Math.round(visibleCandleCount * 0.3)) : 20;
  const timeOffset = candleOffset * timeframeMs;
  const priceOffset = chartPriceRange
    ? (chartPriceRange.max - chartPriceRange.min) * 0.15
    : centerPrice * 0.03;
  const t = verticalLineInitialTimestamp;

  if (name === "parallelChannel") {
    return {
      kind: "three-point",
      point1: { timestamp: t - timeOffset, price: centerPrice - priceOffset },
      point2: { timestamp: t + timeOffset, price: centerPrice + priceOffset },
      point3: { timestamp: t, price: centerPrice - priceOffset * 3 },
    };
  }

  if (name === "fibonacciRetracement") {
    return {
      kind: "two-point",
      point1: { timestamp: t - timeOffset, price: centerPrice + priceOffset },
      point2: { timestamp: t + timeOffset, price: centerPrice - priceOffset },
    };
  }

  const point1: Point = { timestamp: t - timeOffset, price: centerPrice - priceOffset };
  const point2: Point = { timestamp: t + timeOffset, price: centerPrice + priceOffset };
  return { kind: "two-point", point1, point2 };
}
